import React, { useMemo } from 'react';
import './reviews-table.css'; // already exists; ensures compact inputs

const EmptyStyle = () => (
  <style>{`
    .or-empty {
      background: rgba(255, 193, 7, 0.15); /* warning-subtle */
      border-color: rgba(255, 193, 7, 0.35);
    }
  `}</style>
);

export default function OverallRatingCard({
                                              overall,
                                              reviewTypeName,
                                              role,                 // 'employee' | 'manager'
                                              ratings,              // [{id, name, code, description}]
                                              eligibility,          // { ok, errors: [] }
                                              onSaveEmployee,       // ({ rating_id, description }) => Promise
                                              onSaveManager,        // ({ rating_id, description }) => Promise
                                              onAccept,             // () => Promise
                                          }) {
    const isEmployee = role === 'employee';
    const isManager  = role === 'manager';

    const employeeAccepted = overall?.employee_accepted_at;
    const managerAccepted  = overall?.manager_accepted_at;

    const autoExists = overall?.auto_score != null && overall?.auto_rating_id != null;

    const canAccept = useMemo(() => {
        if (!eligibility?.ok) return false;
        if (!autoExists) return false;
        if (isEmployee && employeeAccepted) return false;
        if (isManager  && managerAccepted)  return false;
        return true;
    }, [eligibility, autoExists, isEmployee, isManager, employeeAccepted, managerAccepted]);

    const PlaceholderRating = () => (
        <div className="fw-semibold" style={{ opacity: 0.55 }}>Not yet rated</div>
    );

    const PlaceholderDesc = () => (
        <div className="text-muted" style={{ opacity: 0.7 }}>Performance Description to reflect here once ratings have been done.</div>
    );

    const employeeRating = ratings.find(r => String(r.id) === String(overall?.employee_rating_id));
    const managerRating  = ratings.find(r => String(r.id) === String(overall?.manager_rating_id));
    const autoRating     = ratings.find(r => String(r.id) === String(overall?.auto_rating_id));

    return (
        <>
          <EmptyStyle />
          <div className="card mt-3">
              <div className="card-header d-flex align-items-center justify-content-between">
                  <h5 className="card-titles">Overall rating for: {reviewTypeName}</h5>
                  <div className="d-flex align-items-center gap-2">
                      {isEmployee && employeeAccepted && <span className="badge bg-success">Accepted {new Date(overall.employee_accepted_at).toLocaleString()}</span>}
                      {isManager  && managerAccepted  && <span className="badge bg-success">Accepted {new Date(overall.manager_accepted_at).toLocaleString()}</span>}
                      <button
                          className="btn btn-sm btn-primary"
                          disabled={!canAccept}
                          onClick={onAccept}
                          title={!eligibility?.ok ? (eligibility?.errors?.[0] || 'Not eligible') : undefined}
                      >
                          {isEmployee ? 'Accept as Employee' : 'Accept as Manager'}
                      </button>
                  </div>
              </div>

              <div className="card-body">
                  <div className="row gy-3">
                      {/* Employee column */}
                      <div className="col-md-4">
                          <div className="fw-semibold mb-2">Employee</div>
                          <div className="mb-2">
                              <label className="form-label form-label-sm">Rating</label>
                              <div className="form-control form-control-sm" disabled>
                                {employeeRating ? employeeRating.name : <PlaceholderRating />}
                              </div>
                          </div>
                          <div>
                              <label className="form-label form-label-sm">Description</label>
                              <div className="form-control form-control-sm" disabled>
                                {overall?.employee_description ? overall.employee_description : <PlaceholderDesc />}
                              </div>
                          </div>
                          <div className="mt-2">
                            <label className="form-label form-label-sm">Accepted</label>
                            <div className={`form-control form-control-sm ${!employeeAccepted ? 'or-empty' : ''}`} disabled>
                              {employeeAccepted ? new Date(overall.employee_accepted_at).toLocaleString() : '—'}
                            </div>
                          </div>
                      </div>

                      {/* Manager column */}
                      <div className="col-md-4">
                          <div className="fw-semibold mb-2">Manager</div>
                          <div className="mb-2">
                              <label className="form-label form-label-sm">Rating</label>
                              <div className="form-control form-control-sm" disabled>
                                {managerRating ? managerRating.name : <PlaceholderRating />}
                              </div>
                          </div>
                          <div>
                              <label className="form-label form-label-sm">Description</label>
                              <div className="form-control form-control-sm" disabled>
                                {overall?.manager_description ? overall.manager_description : <PlaceholderDesc />}
                              </div>
                          </div>
                          <div className="mt-2">
                            <label className="form-label form-label-sm">Accepted</label>
                            <div className={`form-control form-control-sm ${!managerAccepted ? 'or-empty' : ''}`} disabled>
                              {managerAccepted ? new Date(overall.manager_accepted_at).toLocaleString() : '—'}
                            </div>
                          </div>
                      </div>

                      {/* Auto column */}
                      <div className="col-md-4">
                          <div className="fw-semibold mb-2">Auto (computed)</div>
                          <div className="mb-2">
                              <label className="form-label form-label-sm">Score</label>
                              <div className="form-control form-control-sm" disabled>
                                  {overall?.auto_score != null ? overall.auto_score : <span className="text-muted">—</span>}
                              </div>
                          </div>
                          <div className="mb-2">
                              <label className="form-label form-label-sm">Rating</label>
                              <div className="form-control form-control-sm" disabled>
                                  {autoRating ? autoRating.name : (autoExists ? '—' : <PlaceholderRating/>)}
                              </div>
                          </div>
                          <div>
                              <label className="form-label form-label-sm">Description</label>
                              <div className="form-control form-control-sm" disabled>
                                  {overall?.auto_description
                                      ? overall.auto_description
                                      : <span className="text-muted">—</span>}
                              </div>
                          </div>
                      </div>
                  </div>

                  {!eligibility?.ok && (
                      <div className="alert alert-warning mt-3 mb-0">
                          <div className="fw-semibold mb-1">You can’t accept yet:</div>
                          <ul className="mb-0">
                              {eligibility.errors?.map((e, i) => <li key={i}>{e}</li>)}
                          </ul>
                      </div>
                  )}
              </div>
          </div>
        </>
    );
}
